import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { getAuthHeaders, clearAuth, getUser, saveAuth, getToken, type User } from '../utils/auth';
import type { ApiResponse } from '../types/api';
import AppShell from './AppShell';
import { Input } from './Input';
import './UserProfileEdit.css';

const UserProfileEdit: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const currentUser = getUser();
      if (currentUser) {
        setUser(currentUser);
        setUsername(currentUser.username);
        setEmail(currentUser.email);
      }

      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: getAuthHeaders(),
      });

      if (response.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }

      if (!response.ok) {
        throw new Error('获取用户信息失败');
      }

      const data: ApiResponse<User> = await response.json();
      if (data.success && data.data) {
        setUser(data.data);
        setUsername(data.data.username);
        setEmail(data.data.email);
        // 更新 localStorage
        const token = getToken();
        if (token) {
          saveAuth({ token, user: data.data });
        }
      }
    } catch (err) {
      console.error('加载用户信息失败:', err);
      setError('加载用户信息失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    // Client-side validation to provide consistent, friendly messages
    if (!username || username.trim().length === 0) {
      setError('用户名不能为空，请输入用户名');
      setSaving(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/user/update`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ username, email }),
      });

      if (response.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }

      const data: ApiResponse<User> = await response.json();

      if (data.success && data.data) {
        // 更新 localStorage
        const token = getToken();
        if (token) {
          saveAuth({ token, user: data.data });
        }
        setSuccess('保存成功');
        // 3秒后自动清除成功消息
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || '更新失败');
      }
    } catch (err) {
      setError('网络错误，请稍后重试');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="user-profile-edit-container">
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: 'white',
          fontSize: '18px'
        }}>
          加载中...
        </div>
      </div>
    );
  }

  const header = (
    <div className="edit-header app-shell-header-content">
      <button
        onClick={() => navigate('/profile')}
        className="back-button"
        title="取消并返回"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12"></line>
          <polyline points="12 19 5 12 12 5"></polyline>
        </svg>
      </button>
      <h1>编辑信息</h1>
    </div>
  );

  return (
    <AppShell header={header} footer={null}>
      <div className="user-profile-edit-container">
        <div className="video-edit-content">
        <form onSubmit={handleSubmit} className="edit-form" noValidate>
          <div className="form-group">
            <label htmlFor="username">用户名</label>
            <Input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              placeholder="请输入用户名"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">邮箱</label>
            <Input
              type="email"
              id="email"
              value={email}
              readOnly
              className="readonly-input"
            />
          </div>

          {success && <div className="success-message">{success}</div>}
          {error && <div className="error-message">{error}</div>}

          <div className="form-actions">
            <button
              type="submit"
              disabled={saving}
              className="save-button"
            >
              {saving ? '保存中...' : '保存'}
            </button>
          </div>
        </form>
        </div>
      </div>
    </AppShell>
  );
};

export default UserProfileEdit;
